/**
 * useFormValidation Hook
 *
 * Client-side validation with toast feedback (Phase 2 - stub implementation).
 * Full implementation in T017.
 */

import { useCallback } from 'react';
import type { z } from 'zod';
import toast from 'react-hot-toast';

export interface UseFormValidationOptions {
  showToastOnError?: boolean;
  toastDuration?: number;
}

export interface ValidationResult<T> {
  valid: boolean;
  data?: T;
  errors?: z.ZodError['errors'];
}

export function useFormValidation<T extends z.ZodTypeAny>(
  schema: T,
  options: UseFormValidationOptions = {}
): {
  validate: (data: unknown) => ValidationResult<z.infer<T>>;
} {
  const { showToastOnError = true, toastDuration = 5000 } = options;

  const validate = useCallback(
    (data: unknown): ValidationResult<z.infer<T>> => {
      const result = schema.safeParse(data);
      if (result.success) {
        return { valid: true, data: result.data as z.infer<T> };
      }
      const errors = result.error.errors;
      const firstError = errors[0];
      const message = firstError?.message ?? 'Validation failed';
      if (showToastOnError) {
        toast.error(message, { duration: toastDuration });
      }
      return { valid: false, errors };
    },
    [schema, showToastOnError, toastDuration]
  );

  return { validate };
}
